import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditContentModalProps, EditContentOptions } from "../../types";
import { fetchLatestItem } from "../../Api/services/content/fetchContent";
import { createLogger } from "../../../utils/logger";
import { deriveSmartListOptions } from "./smartListState";
import { EditContentFormState, useEditContentFormState } from "./useEditContentFormState";

const logger = createLogger("EditContentModal");

const interpretFetchError = (error: unknown): { userMessage: string; developerMessage: string } => {
    const developerMessage =
        error instanceof Error ? error.message : "Unable to contact the Vimond API. Please retry.";
    return {
        userMessage:
            "We couldn’t refresh this item from Vimond automatically. You can retry or edit using cached data.",
        developerMessage,
    };
};

export type UseEditContentModalStateResult = {
    isLoading: boolean;
    fetchErrorMessage: string | null;
    fetchErrorDetails: string | null;
    isFallbackEditing: boolean;
    formState: EditContentFormState;
    handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
    isSaving: boolean;
    handleRetry: () => void;
    handleUseCachedData: () => void;
};

type UseEditContentModalStateParams = Pick<
    EditContentModalProps,
    "item" | "onEditContent" | "onClose"
>;

export const useEditContentModalState = ({
    item,
    onEditContent,
    onClose,
}: UseEditContentModalStateParams): UseEditContentModalStateResult => {
    const { formState, latestItem, applyItemData, smartListState } = useEditContentFormState(item);

    const [isLoading, setIsLoading] = useState(true);
    const [fetchErrorMessage, setFetchErrorMessage] = useState<string | null>(null);
    const [fetchErrorDetails, setFetchErrorDetails] = useState<string | null>(null);
    const [isFallbackEditing, setIsFallbackEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(false);

    const normalizedSmartList = useMemo(
        () => deriveSmartListOptions(smartListState),
        [smartListState],
    );

    const normalizedContentType = formState.normalizedContentType;

    const loadLatestItem = useCallback(() => {
        let isMounted = true;

        void (async () => {
            setIsLoading(true);
            setFetchErrorMessage(null);
            setFetchErrorDetails(null);
            setIsFallbackEditing(false);

            try {
                const freshItem = await fetchLatestItem(item.listId);
                if (!isMounted) {
                    return;
                }
                applyItemData(freshItem);
                setIsLoading(false);
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                const interpreted = interpretFetchError(error);
                setFetchErrorMessage(interpreted.userMessage);
                setFetchErrorDetails(interpreted.developerMessage);
                setIsLoading(false);
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [applyItemData, item.listId]);

    useEffect(() => loadLatestItem(), [loadLatestItem]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isSavingRef.current) {
                return;
            }

            isSavingRef.current = true;
            setIsSaving(true);
            let shouldCloseModal = false;

            try {
                const options: EditContentOptions = {};

                if (normalizedContentType === "list") {
                    options.alias = formState.alias;
                } else if (normalizedContentType === "list_smart") {
                    options.smartListTemplate = smartListState.templateId;
                    options.smartListAdapterId = smartListState.adapterId ?? undefined;
                    options.smartListSortOption = smartListState.sortOption;

                    if (normalizedSmartList.normalizedGenre) {
                        options.smartListGenre = normalizedSmartList.normalizedGenre;
                    }
                    if (normalizedSmartList.normalizedLabels?.length) {
                        options.smartListLabels = normalizedSmartList.normalizedLabels;
                    }
                } else if (normalizedContentType === "page") {
                    options.pageSlug = formState.pageSlug;
                } else if (normalizedContentType === "menu") {
                    options.layoutMenuPosition = formState.layoutMenuPosition;
                } else if (normalizedContentType === "menu_item") {
                    options.position = formState.position;
                }

                await onEditContent(formState.title, options);
                shouldCloseModal = true;
            } catch (error) {
                logger.error("Failed to save content item", error);
            } finally {
                setIsSaving(false);
                isSavingRef.current = false;
                if (shouldCloseModal) {
                    onClose();
                }
            }
        },
        [
            formState.alias,
            formState.layoutMenuPosition,
            formState.pageSlug,
            formState.position,
            formState.title,
            normalizedContentType,
            normalizedSmartList.normalizedGenre,
            normalizedSmartList.normalizedLabels,
            onClose,
            onEditContent,
            smartListState.adapterId,
            smartListState.sortOption,
            smartListState.templateId,
        ],
    );

    const handleUseCachedData = useCallback(() => {
        setFetchErrorMessage(null);
        setFetchErrorDetails(null);
        setIsFallbackEditing(true);
        applyItemData(latestItem);
    }, [applyItemData, latestItem]);

    return {
        isLoading,
        fetchErrorMessage,
        fetchErrorDetails,
        isFallbackEditing,
        formState,
        handleSubmit,
        isSaving,
        handleRetry: () => {
            void loadLatestItem();
        },
        handleUseCachedData,
    };
};
